import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Badge,
  Divider,
  CircularProgress,
  IconButton,
  ListItemButton,
  Button,
} from '@mui/material';
import { PersonAdd } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ChatController } from '../../hooks/useChat';
import { UserAvatar } from '../common/UserAvatar.tsx';
import { ChatStatusBadge } from './ChatStatusBadge';

export const ConversationsList: React.FC<{
  onCreateConversation: () => void;
  chat: ChatController;
}> = ({ onCreateConversation, chat }) => {
  const navigate = useNavigate();
  const {
    conversations,
    activeConversationId,
    loadingConversations,
    formatMessageTime,
    truncateMessage,
  } = chat;

  const showLoader = loadingConversations && conversations.length === 0;

  if (showLoader) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  const handleConversationClick = (conversationId: number) => {
    navigate(`/chat/${conversationId}`);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight="bold">Чати</Typography>
        <IconButton onClick={onCreateConversation} color="primary" size="small">
          <PersonAdd />
        </IconButton>
      </Box>
      <Divider />
      <List sx={{ p: 0, overflowY: 'auto', flexGrow: 1 }}>
        {conversations.map((conversation) => (
          <ListItem
            key={conversation.id}
            disablePadding
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <ListItemButton
              selected={conversation.id === activeConversationId}
              onClick={() => handleConversationClick(conversation.id)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                py: 1.5,
              }}
            >
              <ListItemAvatar>
                <ChatStatusBadge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  variant="dot"
                  status={conversation.participantStatus}
                  invisible={!conversation.participantStatus || conversation.participantStatus === 'offline'}
                >
                  <UserAvatar 
                    path={conversation.participantAvatar} 
                    name={conversation.participantName} 
                    sx={{ width: 48, height: 48 }}
                  />
                </ChatStatusBadge>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600, maxWidth: '70%' }}>
                      {conversation.participantName}
                    </Typography>
                    {conversation.lastMessageAt && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {formatMessageTime(conversation.lastMessageAt)}
                      </Typography>
                    )}
                  </Box>
                }
                secondary={
                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                    <Typography 
                      variant="body2" 
                      color={Number(conversation.unreadCount) > 0 ? 'text.primary' : 'text.secondary'}
                      noWrap
                      sx={{ 
                        maxWidth: '80%', 
                        fontWeight: Number(conversation.unreadCount) > 0 ? 500 : 400 
                      }}
                    >
                      {conversation.lastMessage
                        ? truncateMessage(conversation.lastMessage, 40)
                        : 'Немає повідомлень'}
                    </Typography>
                    {Number(conversation.unreadCount) > 0 && (
                      <Badge 
                        badgeContent={conversation.unreadCount} 
                        color="primary" 
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
        {conversations.length === 0 && (
          <Box p={4} textAlign="center" display="flex" flexDirection="column" alignItems="center">
            <Typography color="text.secondary" gutterBottom>
              У вас поки немає активних чатів.
            </Typography>
            <Button variant="outlined" startIcon={<PersonAdd />} onClick={onCreateConversation} sx={{ mt: 1 }}>
              Почати спілкування
            </Button>
          </Box>
        )}
      </List>
    </Box>
  );
};
